/**
 * Backups - for administrators.
 *
 * Every backup holds the whole system: the database (moulds, customers,
 * locations, photo records, scans, people, history) and every photo file.
 * Each is complete on its own, yet costs only what changed since the last one:
 * the backup store keeps every piece of data once, so a night with twenty new
 * moulds adds about twenty moulds' worth. See docs/BACKUP.md.
 *
 * "Success" is only shown for a backup that was read back afterwards and
 * matched: restored into a scratch database, every table's row count compared,
 * every photo found.
 *
 * Restore replaces every record in the system, so it is not a button: the
 * screen gives the command, which restores into a new database by default and
 * takes a safety backup before touching live data.
 */
import { useCallback, useEffect, useState, type ChangeEvent, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, DatabaseBackup, Loader2, RotateCcw, ShieldCheck, XCircle } from 'lucide-react';
import type { BackupRunDto, BackupSettingsDto, BackupStatusDto } from '@mouldtrack/shared';
import { Page } from '../../components/layout/Page.js';
import { Button } from '../../components/ui/button.js';
import { Checkbox, Field, Input, Select } from '../../components/ui/field.js';
import { Badge, Card, CardBody, CardHeader, CardTitle, Code, EmptyState } from '../../components/ui/primitives.js';
import { Dialog } from '../../components/ui/dialog.js';
import { ApiClientError, backupStatus, isNetworkError, startBackup, updateBackupSettings, verifyBackup } from '../../api/client.js';
import { formatBytes, formatDateTime, formatRelative } from '../../lib/format.js';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ZONES = ['Asia/Kolkata', 'Asia/Dubai', 'Europe/London', 'UTC'];

function messageOf(e: unknown, fallback: string) {
  if (isNetworkError(e)) return 'No connection to the server.';
  return e instanceof ApiClientError ? e.message : fallback;
}

export function BackupsPage() {
  const [status, setStatus] = useState<BackupStatusDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [restoreFor, setRestoreFor] = useState<BackupRunDto | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await backupStatus());
      setError(null);
    } catch (e) {
      setError(messageOf(e, 'Could not load backups.'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // While something is running, follow it.
  const active = !!status?.running || !!status?.verifying;
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => void load(), 3000);
    return () => clearInterval(t);
  }, [active, load]);

  async function backupNow() {
    setStarting(true);
    setError(null);
    try {
      await startBackup();
      await load();
    } catch (e) {
      setError(messageOf(e, 'Could not start a backup.'));
    } finally {
      setStarting(false);
    }
  }

  async function verify(run: BackupRunDto) {
    setError(null);
    try {
      await verifyBackup(run.id);
      await load();
    } catch (e) {
      setError(messageOf(e, 'Could not start the check.'));
    }
  }

  const lastGood = status?.runs.find((r) => r.status === 'SUCCESS');

  return (
    <Page
      title="Backups"
      description="Every backup restores the whole system. Each one stores only what changed since the last."
      actions={
        <Button variant="primary" onClick={() => void backupNow()} loading={starting || !!status?.running} disabled={!status?.configured || active}>
          <DatabaseBackup className="size-4" /> {status?.running ? 'Backing up…' : 'Back up now'}
        </Button>
      }
    >
      {error ? (
        <p role="alert" className="mb-3 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {!status ? (
        <EmptyState icon={<DatabaseBackup />} title="Loading…" description="" />
      ) : (
        <div className="flex flex-col gap-4">
          {!status.configured ? (
            <div className="flex items-start gap-2 rounded-xl border border-warning/40 bg-warning-subtle px-4 py-3 text-sm">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
              <div>
                <p className="font-medium">Backups are not set up on the server.</p>
                <p className="text-muted-foreground">{status.problem} See docs/BACKUP.md for the three settings it needs.</p>
              </div>
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
            <Card>
              <CardHeader>
                <CardTitle>Status</CardTitle>
              </CardHeader>
              <CardBody className="flex flex-col gap-2 text-sm">
                <Row label="Last good backup">
                  {lastGood ? `${formatDateTime(lastGood.startedAt)} (${formatRelative(lastGood.startedAt)})` : 'None yet'}
                </Row>
                <Row label="Next scheduled">{status.nextRunAt ? formatDateTime(status.nextRunAt) : status.settings.enabled ? '-' : 'Schedule is off'}</Row>
                <Row label="Stored at">{status.repository ? <Code className="text-xs">{status.repository}</Code> : '-'}</Row>
                <Row label="Backup store size">{lastGood ? formatBytes(lastGood.repoBytes) : '-'}</Row>
                {status.running ? (
                  <p className="flex items-center gap-2 text-primary">
                    <Loader2 className="size-4 animate-spin" aria-hidden /> A backup started {formatRelative(status.running.startedAt)} is running.
                  </p>
                ) : null}
                {status.verifying ? (
                  <p className="flex items-center gap-2 text-primary">
                    <Loader2 className="size-4 animate-spin" aria-hidden /> Checking a backup - restoring it into a scratch database…
                  </p>
                ) : null}
              </CardBody>
            </Card>

            <SettingsCard settings={status.settings} onSaved={() => void load()} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>History</CardTitle>
              <span className="text-xs text-muted-foreground">newest first</span>
            </CardHeader>
            {status.runs.length === 0 ? (
              <CardBody>
                <p className="text-sm text-muted-foreground">No backups yet.</p>
              </CardBody>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] border-collapse text-sm">
                  <thead className="bg-surface-sunken">
                    <tr>
                      <Th />
                      <Th>Date</Th>
                      <Th>Type</Th>
                      <Th className="text-right">Mould records</Th>
                      <Th className="text-right">Photos</Th>
                      <Th>Status</Th>
                      <Th className="text-right">Size</Th>
                      <Th />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {status.runs.map((run) => (
                      <RunRow
                        key={run.id}
                        run={run}
                        busy={active}
                        verifying={status.verifying === run.id}
                        onVerify={() => void verify(run)}
                        onRestore={() => setRestoreFor(run)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      <RestoreDialog run={restoreFor} onClose={() => setRestoreFor(null)} />
    </Page>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{children}</span>
    </div>
  );
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-3 py-2 text-left text-xs font-semibold text-muted-foreground ${className}`}>{children}</th>;
}

function StatusBadge({ run, verifying }: { run: BackupRunDto; verifying: boolean }) {
  if (verifying) return <Badge tone="info"><Loader2 className="size-3 animate-spin" /> Checking</Badge>;
  if (run.status === 'RUNNING') return <Badge tone="info"><Loader2 className="size-3 animate-spin" /> Running</Badge>;
  if (run.status === 'FAILED') return <Badge tone="danger"><XCircle className="size-3" /> Failed</Badge>;
  if (run.removedAt) return <Badge tone="neutral">Removed by retention</Badge>;
  if (run.verifyResult?.startsWith('FAILED')) return <Badge tone="danger"><XCircle className="size-3" /> Check failed</Badge>;
  return <Badge tone="success"><CheckCircle2 className="size-3" /> Verified</Badge>;
}

function RunRow({
  run,
  busy,
  verifying,
  onVerify,
  onRestore,
}: {
  run: BackupRunDto;
  busy: boolean;
  verifying: boolean;
  onVerify: () => void;
  onRestore: () => void;
}) {
  const [open, setOpen] = useState(false);
  const full = !run.parentSnapshotId;
  return (
    <>
      <tr className="align-top">
        <td className="px-2 py-2">
          <button type="button" onClick={() => setOpen((o) => !o)} aria-label={open ? 'Hide details' : 'View details'} className="rounded p-1 hover:bg-surface-sunken">
            {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          </button>
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          {formatDateTime(run.startedAt)}
          <span className="block text-xs text-muted-foreground">{run.trigger === 'MANUAL' ? `by ${run.userName ?? 'someone'}` : 'scheduled'}</span>
        </td>
        <td className="px-3 py-2">{run.status === 'FAILED' && !run.snapshotId ? '-' : full ? 'Full' : 'Incremental'}</td>
        <td className="px-3 py-2 text-right tabular">
          {full ? run.mouldsTotal : `+${run.mouldsAdded}`}
          <span className="block text-xs text-muted-foreground">
            {full ? 'all, incl. deleted' : `${run.mouldsChanged} changed · ${run.mouldsTotal} in total`}
          </span>
        </td>
        <td className="px-3 py-2 text-right tabular">
          {full ? run.photosTotal : `+${run.photosAdded}`}
          {!full ? <span className="block text-xs text-muted-foreground">{run.photosTotal} in total</span> : null}
        </td>
        <td className="px-3 py-2">
          <StatusBadge run={run} verifying={verifying} />
        </td>
        <td className="px-3 py-2 text-right tabular whitespace-nowrap">
          {run.snapshotId ? formatBytes(run.bytesAdded) : '-'}
          {run.snapshotId ? <span className="block text-xs text-muted-foreground">restores {formatBytes(run.bytesTotal)}</span> : null}
        </td>
        <td className="px-3 py-2 text-right whitespace-nowrap">
          {run.snapshotId && run.status === 'SUCCESS' && !run.removedAt ? (
            <>
              <Button variant="ghost" size="sm" onClick={onVerify} disabled={busy}>
                <ShieldCheck className="size-4" /> Verify
              </Button>
              <Button variant="ghost" size="sm" onClick={onRestore}>
                <RotateCcw className="size-4" /> Restore
              </Button>
            </>
          ) : null}
        </td>
      </tr>
      {open ? (
        <tr>
          <td />
          <td colSpan={7} className="px-3 pb-3 text-sm">
            <div className="grid gap-3 rounded-lg bg-surface-sunken p-3 md:grid-cols-2">
              <div className="flex flex-col gap-1">
                <Row label="Snapshot">{run.snapshotId ? <Code className="text-xs">{run.snapshotId.slice(0, 12)}</Code> : '-'}</Row>
                <Row label="Builds on">{run.parentSnapshotId ? <Code className="text-xs">{run.parentSnapshotId.slice(0, 12)}</Code> : 'nothing - first backup'}</Row>
                <Row label="Files new / changed / same">{`${run.filesNew} / ${run.filesChanged} / ${run.filesUnchanged}`}</Row>
                <Row label="Stored by this backup">{formatBytes(run.bytesAdded)}</Row>
                <Row label="Finished">{run.finishedAt ? formatDateTime(run.finishedAt) : '-'}</Row>
                {run.verifiedAt ? <Row label="Last checked">{formatDateTime(run.verifiedAt)}</Row> : null}
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-xs font-semibold text-muted-foreground">Records in this backup</p>
                <div className="grid grid-cols-2 gap-x-4 text-xs">
                  {Object.entries(run.counts)
                    .filter(([t]) => !t.startsWith('_'))
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 16)
                    .map(([table, n]) => (
                      <span key={table} className="flex justify-between gap-2">
                        <span className="truncate text-muted-foreground">{table}</span>
                        <span className="tabular">{n}</span>
                      </span>
                    ))}
                </div>
              </div>
              {run.verifyResult ? <p className="md:col-span-2 text-xs">{run.verifyResult}</p> : null}
              {run.error ? <p className="md:col-span-2 rounded bg-danger-subtle px-2 py-1 text-xs text-danger">{run.error}</p> : null}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function SettingsCard({ settings, onSaved }: { settings: BackupSettingsDto; onSaved: () => void }) {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => setForm(settings), [settings]);
  const dirty = JSON.stringify(form) !== JSON.stringify(settings);

  async function save() {
    setSaving(true);
    setMessage(null);
    try {
      const { updatedAt: _u, ...input } = form;
      await updateBackupSettings(input);
      setMessage({ ok: true, text: 'Saved.' });
      onSaved();
    } catch (e) {
      setMessage({ ok: false, text: messageOf(e, 'Could not save.') });
    } finally {
      setSaving(false);
    }
  }

  const num = (k: 'keepDaily' | 'keepWeekly' | 'keepMonthly') => ({
    type: 'number' as const,
    min: 0,
    max: 1000,
    value: String(form[k]),
    onChange: (e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: Math.max(0, Math.floor(Number(e.target.value) || 0)) }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Schedule and retention</CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-3">
        <Checkbox
          label="Back up automatically"
          checked={form.enabled}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
        />
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="How often">
            <Select
              value={form.frequency}
              onChange={(e) => setForm({ ...form, frequency: e.target.value as BackupSettingsDto['frequency'] })}
              options={[
                { value: 'DAILY', label: 'Every day' },
                { value: 'WEEKLY', label: 'Every week' },
              ]}
            />
          </Field>
          {form.frequency === 'WEEKLY' ? (
            <Field label="On">
              <Select
                value={String(form.weekday)}
                onChange={(e) => setForm({ ...form, weekday: Number(e.target.value) })}
                options={WEEKDAYS.map((d, i) => ({ value: String(i), label: d }))}
              />
            </Field>
          ) : null}
          <Field label="At">
            <Input type="time" value={form.timeOfDay} onChange={(e) => setForm({ ...form, timeOfDay: e.target.value })} />
          </Field>
          <Field label="Time zone">
            <Select
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              options={[...new Set([form.timezone, ...ZONES])].map((z) => ({ value: z, label: z }))}
            />
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Keep daily backups" hint="days">
            <Input {...num('keepDaily')} />
          </Field>
          <Field label="Keep weekly backups" hint="weeks">
            <Input {...num('keepWeekly')} />
          </Field>
          <Field label="Keep monthly backups" hint="months">
            <Input {...num('keepMonthly')} />
          </Field>
        </div>
        <p className="text-xs text-muted-foreground">
          Older backups are tidied away after each new one succeeds. Where backups are stored is set on the server, not
          here.
        </p>
        <div className="flex items-center gap-3">
          <Button variant="primary" onClick={() => void save()} loading={saving} disabled={!dirty}>
            Save
          </Button>
          {message ? <span className={message.ok ? 'text-sm text-success' : 'text-sm text-danger'}>{message.text}</span> : null}
        </div>
      </CardBody>
    </Card>
  );
}

function RestoreDialog({ run, onClose }: { run: BackupRunDto | null; onClose: () => void }) {
  const snap = run?.snapshotId?.slice(0, 8) ?? '';
  return (
    <Dialog
      open={!!run}
      onOpenChange={(open) => !open && onClose()}
      title="Restore this backup"
      description={run ? `Taken ${formatDateTime(run.startedAt)} - ${run.mouldsTotal} moulds, ${run.photosTotal} photos.` : undefined}
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-3 pb-2 text-sm">
        <p>
          Restoring is done on the server, by an administrator with access to it. It is not a button here on purpose: a
          restore replaces every record in the system, for everyone.
        </p>
        <p className="font-medium">1. Restore beside the live system and check it (changes nothing live):</p>
        <p className="text-muted-foreground">From the MouldTrack folder on the server:</p>
        <pre className="overflow-x-auto rounded-lg bg-surface-sunken p-3 text-xs">{`scripts/restore-backup.sh ${snap}`}</pre>
        <p className="text-muted-foreground">
          This creates a new database and photo folder from the backup and compares every table with the backup&apos;s
          record of itself.
        </p>
        <p className="font-medium">2. To replace the live data with this backup:</p>
        <pre className="overflow-x-auto rounded-lg bg-surface-sunken p-3 text-xs">{`scripts/restore-backup.sh ${snap} --into-live`}</pre>
        <p className="text-muted-foreground">
          It backs up the current state first (so the restore can itself be undone), stops the application, restores,
          checks, and starts it again. It asks you to type the backup id to confirm.
        </p>
      </div>
    </Dialog>
  );
}
